import React, { useCallback } from 'react';
import { DatePicker, Spin } from 'antd';
import {
  Autocomplete,
  Button,
  Input,
  Col,
} from '@peoplegrove/pg-components';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useUserFeedbackFiltersContext } from '../../context/UserFeedbackFiltersContext';
import useFetchUserFeedback from '../../hooks/useFetchUserFeedback';
import useFetchTagsList from '../../hooks/useFetchTagsList';
import UserFeedbackMoreFiltersDropdown from '../../components/UserFeedbackMoreFiltersDropdown';
import { disabledDate } from '../../utils/core';
import classes from './UserFeedback.module.scss';

const { RangePicker } = DatePicker;

const UserFeedbackFilters = () => {
  const {
    pendingFilters,
    setUserFeedbackFilters,
    resetFilters,
    applyFilters,
    isDirty,
    enableClearAll,
  } = useUserFeedbackFiltersContext();
  const { isLoading } = useFetchUserFeedback();
  const { data: tagsList, isLoading: isTagsListLoading } = useFetchTagsList();

  const handleFilterChange = useCallback(
    (filter, value) => {
      setUserFeedbackFilters(filter, value);
    },
    [setUserFeedbackFilters]
  );

  const handleClearAllFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      applyFilters();
    }
  };

  const handleSearchChange = (value) => {
    handleFilterChange('searchTerm', value);
  };

  const handleDateRangeChange = useCallback(
    (dateObj, dateStrs) => {
      if (isEmpty(dateObj)) {
        handleFilterChange('dateRange', null);
      } else {
        const [startDate, endDate] = dateStrs;
        handleFilterChange('dateRange', { start: startDate, end: endDate });
      }
    },
    [handleFilterChange]
  );

  if (isTagsListLoading) {
    return <Spin size="large" />;
  }

  return (
    <div className={classes.filtersContainer}>
      <Col span={4}>
        <Input
          placeholder="Search by name"
          value={pendingFilters.searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          size="small"
        />
      </Col>
      <Col span={4} className={classes.dateFilter}>
        <RangePicker
          value={
            pendingFilters.dateRange
              ? [
                  moment(pendingFilters.dateRange.start),
                  moment(pendingFilters.dateRange.end),
                ]
              : []
          }
          onChange={handleDateRangeChange}
          disabledDate={disabledDate}
          format="MM/DD/YYYY"
          allowClear
        />
      </Col>
      <Col span={4} align="center">
        <Autocomplete
          placeholder="Tags"
          multiple={true}
          value={pendingFilters.tags}
          onChange={value => handleFilterChange('tags', value)}
          allowClear
          size="small"
          options={tagsList.map(item => ({
            value: item,
            label: item,
          }))}
          maxPillCount={1}
        ></Autocomplete>
      </Col>
      <Col span={2} align="center">
        <UserFeedbackMoreFiltersDropdown />
      </Col>
      <Col span={6} align="center" className={classes.filterButtonsContainer}>
        <Button
          type="secondary"
          disabled={isLoading || !isDirty}
          onClick={applyFilters}
          size="small"
        >
          Apply Filters
        </Button>

        <Button
          type="secondary"
          color="red"
          size="small"
          disabled={isLoading || !enableClearAll}
          onClick={handleClearAllFilters}
        >
          Clear All
        </Button>
      </Col>
    </div>
  );
};

export default UserFeedbackFilters;
